import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, apiError } from "@/lib/api";
import {
  CurrencyDollar, Users, ArrowsClockwise, Warning, TrendUp,
  Flame, ChartLineUp, ArrowRight, Confetti, Crown,
} from "@phosphor-icons/react";

/**
 * CoachRevenue — snappy revenue snapshot for Jimmy's Monday-morning phone check.
 *
 * Renders everything /api/coach/revenue-dashboard returns in one scroll:
 *  1. Weekly MRR (headline) + breakdown by plan tier
 *  2. Cash collected in the last 7 & 30 days
 *  3. Cancellation rate + top-reason chip (last 30d)
 *  4. Funnel: $7 trials started → still active + OTO purchases
 *  5. Founder-slot burn rate + ETA to sold out
 *  6. Upcoming price rollovers this week
 *
 * All numbers come from the single-shot endpoint — no chained fetches, no
 * external Stripe calls on the request path. Refresh button re-queries.
 */
export default function CoachRevenue() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState("");

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setErr("");
    try {
      const { data } = await http.get("/coach/revenue-dashboard");
      setData(data);
    } catch (e) {
      setErr(apiError(e, "Couldn't load revenue dashboard."));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const refresh = () => { setRefreshing(true); load({ silent: true }); };

  if (loading) {
    return (
      <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="revenue-loading">
        <div className="id-label mb-3">— Coach Command Center —</div>
        <h1 className="id-h1 mb-2">Revenue</h1>
        <p className="text-zinc-500 font-manrope">Crunching the numbers…</p>
      </div>
    );
  }

  if (err && !data) {
    return (
      <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="revenue-error">
        <div className="id-label mb-3">— Coach Command Center —</div>
        <h1 className="id-h1 mb-2">Revenue</h1>
        <div className="border border-[#FF3333] bg-[#FF3333]/10 px-4 py-3 text-sm text-[#FF3333] mb-6">
          {err}
        </div>
        <button onClick={refresh} className="id-btn-secondary" data-testid="revenue-retry-btn">
          <ArrowsClockwise size={16} weight="bold" /> Retry
        </button>
      </div>
    );
  }

  const currency = (data.currency || "aud").toUpperCase();
  const mrr = data.weekly_mrr;
  const cash = data.revenue_collected;
  const cancel = data.cancellations;
  const funnel = data.funnel;
  const founders = data.founders;

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="coach-revenue-page">
      <div className="flex items-start justify-between mb-3 gap-4">
        <div>
          <div className="id-label mb-3">— Coach Command Center —</div>
          <h1 className="id-h1 mb-2">Revenue</h1>
          <p className="text-zinc-400 font-manrope">
            Weekly MRR · cash collected · churn · funnel · founder slots.
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={refreshing}
          className="id-btn-ghost border border-[#262626] hover:border-white shrink-0"
          data-testid="revenue-refresh-btn"
          aria-label="Refresh"
        >
          <ArrowsClockwise size={16} weight="bold" className={refreshing ? "animate-spin" : ""} />
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {err && (
        <div className="border border-[#FF3333] bg-[#FF3333]/10 px-4 py-3 text-sm text-[#FF3333] mb-6" data-testid="revenue-refresh-error">
          {err}
        </div>
      )}

      {/* ---------- Headline: Weekly MRR ---------- */}
      <div className="id-card border-[#FF3333]/40 bg-gradient-to-br from-[#FF3333]/10 to-transparent mb-6" data-testid="revenue-mrr">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="id-label text-[#FF3333] flex items-center gap-2 mb-2">
              <TrendUp size={12} weight="fill" /> Weekly MRR
            </div>
            <div className="font-oswald text-5xl md:text-7xl font-bold text-white tabular-nums leading-none" data-testid="revenue-mrr-amount">
              ${mrr.amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </div>
            <div className="text-zinc-500 text-xs font-manrope mt-2 uppercase tracking-[0.2em]">
              {currency} · projected next 7 days
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="id-label text-zinc-400 mb-1">Active subs</div>
            <div className="font-oswald text-4xl font-bold text-white tabular-nums" data-testid="revenue-active-subs">
              {mrr.active_subs}
            </div>
          </div>
        </div>

        {/* Sub tier breakdown */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6 pt-6 border-t border-[#262626]" data-testid="revenue-mrr-breakdown">
          <BreakdownPill label="$89 / wk (Plan)" value={mrr.breakdown.plan_89} testid="mrr-plan-89" />
          <BreakdownPill label="$59 / wk (Founders)" value={mrr.breakdown.plan_founder_59} accent testid="mrr-founder-59" />
          <BreakdownPill label="$69 / wk (Continued)" value={mrr.breakdown.continued_69} testid="mrr-continued-69" />
          <BreakdownPill label="Other" value={mrr.breakdown.other} muted testid="mrr-other" />
        </div>

        {data.rollovers_next_7d > 0 && (
          <div className="mt-4 flex items-center gap-2 text-xs font-manrope text-zinc-400" data-testid="revenue-rollovers-hint">
            <ArrowRight size={12} weight="bold" className="text-[#FF3333]" />
            <span>
              <span className="text-white">{data.rollovers_next_7d}</span> plan client{data.rollovers_next_7d === 1 ? "" : "s"} rolling
              from $89 → $69 in the next 7 days. Consider a heads-up DM.
            </span>
          </div>
        )}
      </div>

      {/* ---------- Row: Cash collected ---------- */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-testid="revenue-cash-collected">
        <CashCard label="Cash collected · 7d" data={cash.last_7_days} icon={CurrencyDollar} currency={currency} testid="cash-7d" />
        <CashCard label="Cash collected · 30d" data={cash.last_30_days} icon={CurrencyDollar} currency={currency} testid="cash-30d" />
      </div>

      {/* ---------- Row: Churn + Funnel ---------- */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Churn */}
        <div className="id-card" data-testid="revenue-churn">
          <div className="flex items-center justify-between mb-3">
            <div className="id-label flex items-center gap-2">
              <Warning size={12} weight="fill" className="text-[#FF3333]" /> Cancellations · 30d
            </div>
          </div>
          <div className="flex items-baseline gap-3">
            <div className="font-oswald text-4xl font-bold text-white tabular-nums" data-testid="churn-count">
              {cancel.last_30_days}
            </div>
            <div className="text-zinc-500 text-sm font-manrope">
              <span className="text-[#FF3333]" data-testid="churn-rate">{cancel.rate_pct}%</span> of at-risk pool
            </div>
          </div>
          {cancel.top_reason ? (
            <div className="mt-4 pt-4 border-t border-[#262626]" data-testid="churn-top-reason">
              <div className="id-label text-zinc-500 mb-1">Top reason</div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-white font-manrope text-sm">{cancel.top_reason.label}</span>
                <span className="text-zinc-500 text-xs">
                  {cancel.top_reason.count} of {cancel.last_30_days}
                </span>
              </div>
            </div>
          ) : (
            <div className="mt-4 pt-4 border-t border-[#262626] text-zinc-500 text-xs font-manrope italic">
              No cancellations in the last 30 days — nice.
            </div>
          )}
        </div>

        {/* Funnel */}
        <div className="id-card" data-testid="revenue-funnel">
          <div className="id-label flex items-center gap-2 mb-3">
            <ChartLineUp size={12} weight="fill" className="text-[#FF3333]" /> $7 Trial → Plan · 30d
          </div>
          <div className="flex items-baseline gap-3">
            <div className="font-oswald text-4xl font-bold text-white tabular-nums" data-testid="funnel-converted">
              {funnel.trials_converted_30d}
            </div>
            <div className="text-zinc-500 text-sm font-manrope">
              of <span className="text-white">{funnel.trials_started_30d}</span> trials
              <span className="text-[#22C55E] ml-2" data-testid="funnel-conversion-pct">({funnel.conversion_pct}%)</span>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-[#262626] flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-zinc-400 text-xs font-manrope">
              <Confetti size={14} weight="fill" className="text-[#FF3333]" />
              <span>Roadmap Call OTO purchases</span>
            </div>
            <div className="font-oswald text-xl font-bold text-white tabular-nums" data-testid="funnel-oto-count">
              {funnel.oto_purchased_30d}
            </div>
          </div>
        </div>
      </div>

      {/* ---------- Founder slots ---------- */}
      <div className="id-card mb-6" data-testid="revenue-founders">
        <div className="flex items-center justify-between mb-4">
          <div className="id-label flex items-center gap-2">
            <Crown size={12} weight="fill" className="text-[#FF3333]" /> Founder Slots
          </div>
          <div className="text-xs text-zinc-500 font-manrope">
            {founders.claimed} / {founders.cap} claimed
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-full h-3 bg-[#1A1A1A] border border-[#262626] mb-4 overflow-hidden" data-testid="founders-bar">
          <div
            className="h-full bg-gradient-to-r from-[#FF3333] to-[#FF6666] transition-all duration-500"
            style={{ width: `${Math.min(100, (founders.claimed / founders.cap) * 100)}%` }}
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <FoundersStat label="Remaining" value={founders.remaining} testid="founders-remaining" />
          <FoundersStat
            label="Minted last 7d"
            value={founders.burn_rate_7d}
            icon={Flame}
            accent={founders.burn_rate_7d > 0}
            testid="founders-burn-7d"
          />
          <FoundersStat
            label="ETA to sold out"
            value={
              founders.remaining === 0
                ? "SOLD OUT"
                : founders.days_to_full_est == null
                  ? "—"
                  : `~${founders.days_to_full_est}d`
            }
            testid="founders-eta"
          />
        </div>
      </div>

      {/* ---------- Quick jump ---------- */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link to="/coach/clients" className="id-card hover:border-[#FF3333] transition-colors group" data-testid="jump-clients">
          <div className="flex items-center justify-between">
            <div>
              <div className="id-label mb-2">Roster</div>
              <div className="font-oswald text-2xl font-semibold uppercase">Manage Clients</div>
              <p className="text-zinc-500 text-sm mt-1 font-manrope">DM · assign · save-attempt cancellations</p>
            </div>
            <Users size={28} className="text-zinc-600 group-hover:text-[#FF3333]" weight="bold" />
          </div>
        </Link>
        <Link to="/coach/waitlist" className="id-card hover:border-[#FF3333] transition-colors group" data-testid="jump-waitlist">
          <div className="flex items-center justify-between">
            <div>
              <div className="id-label mb-2">Pipeline</div>
              <div className="font-oswald text-2xl font-semibold uppercase">Waitlist Inbox</div>
              <p className="text-zinc-500 text-sm mt-1 font-manrope">Fresh leads · reach out · convert</p>
            </div>
            <ArrowRight size={28} className="text-zinc-600 group-hover:text-[#FF3333]" weight="bold" />
          </div>
        </Link>
      </div>

      <div className="mt-8 text-xs text-zinc-600 font-manrope italic" data-testid="revenue-generated-at">
        Snapshot generated {new Date(data.generated_at).toLocaleString()}. Refresh anytime.
      </div>
    </div>
  );
}

function BreakdownPill({ label, value, accent = false, muted = false, testid }) {
  return (
    <div
      className={
        "border p-3 " +
        (accent
          ? "border-[#FF3333]/50 bg-[#FF3333]/5"
          : muted
            ? "border-[#1A1A1A] bg-[#0A0A0A]"
            : "border-[#262626] bg-[#0F0F0F]")
      }
      data-testid={testid}
    >
      <div className="id-label text-[10px] text-zinc-500 mb-1">{label}</div>
      <div className={"font-oswald text-2xl font-bold tabular-nums " + (muted ? "text-zinc-500" : "text-white")}>
        {value}
      </div>
    </div>
  );
}

function CashCard({ label, data, icon: Icon, currency, testid }) {
  return (
    <div className="id-card" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="id-label flex items-center gap-2">
          <Icon size={12} weight="fill" className="text-[#FF3333]" /> {label}
        </div>
      </div>
      <div className="flex items-baseline gap-3">
        <div className="font-oswald text-4xl font-bold text-white tabular-nums">
          ${data.amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
        </div>
        <div className="text-zinc-500 text-xs font-manrope">
          {currency} · {data.count} payment{data.count === 1 ? "" : "s"}
        </div>
      </div>
    </div>
  );
}

function FoundersStat({ label, value, icon: Icon, accent = false, testid }) {
  return (
    <div className="border border-[#262626] p-3" data-testid={testid}>
      <div className="id-label text-[10px] text-zinc-500 mb-1 flex items-center gap-1">
        {Icon && <Icon size={10} weight="fill" className={accent ? "text-[#FF3333]" : "text-zinc-500"} />}
        {label}
      </div>
      <div className={"font-oswald text-2xl font-bold tabular-nums " + (accent ? "text-[#FF3333]" : "text-white")}>
        {value}
      </div>
    </div>
  );
}
